import { ApiMethod } from '@wajeez-web/stores/base';
import { contentClient, Server } from '@wajeez-web/core/api-base';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import { thunkErrorHandler } from '@wajeez-web/core/utils';

export const getComponentsDatatable: ApiMethod<
  Server.ComponentsLookUpDatatable,
  Server.TableOfComponentsLookUpColumns,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_lookups_datatable_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentById: ApiMethod<
  string,
  Server.ComponentOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );
    const response = await client.components_byId_get_V1(undefined, id);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentByIdMultilingual: ApiMethod<
  string,
  Server.ComponentMultilingualOutput,
  void
> = async (id, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Components_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_Components_multilingualById_get_V1(
        id
      );

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const getAllowedComponentsForCollectionComponentType: ApiMethod<
  {
    searchText?: string | undefined;
    collectionComponentTypeId?: number | undefined;
    collectionComponentTypeName?: string | undefined;
    pageNumber: number | undefined;
    pageSize: number | undefined;
  },
  {
    data: Server.ComponentOutput[];
    meta: {
      currentPage: number | undefined;
      totalCount: number | undefined;
      hasNext: boolean | undefined;
    };
  },
  any
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const {
      searchText,
      collectionComponentTypeId,
      collectionComponentTypeName,
      pageNumber,
      pageSize,
    } = data;

    const response =
      await client.components_allowedForCollectionComponentType_get_V1(
        undefined,
        searchText,
        collectionComponentTypeId,
        collectionComponentTypeName,
        pageNumber,
        pageSize
      );

    if (response.success && response.data) {
      return {
        data: response.data,
        meta: {
          currentPage: response.currentPage,
          totalCount: response.totalCount,
          hasNext: response.hasNextPage,
        },
      };
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentLookups: ApiMethod<
  { searchText?: string },
  Server.ComponentsLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_lookups_get_V1(
      undefined,
      data.searchText
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentComponentLookups: ApiMethod<
  { searchText?: string },
  Server.ComponentsLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_lookups_get_V1(
      undefined,
      data.searchText,
      Server.ComponentUsageType.Component
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentPageBodyLookups: ApiMethod<
  { searchText?: string },
  Server.ComponentsLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_lookups_get_V1(
      undefined,
      data.searchText,
      Server.ComponentUsageType.PageBody
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const getComponentLookupsByComponentTypeIds: ApiMethod<
  { componentTypeIds: number[]; searchText?: string },
  Server.ComponentsLookUpOutput[],
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_lookups_byComponentTypes_get_V1(
      undefined,
      data.componentTypeIds,
      data.searchText
    );

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const createComponentByTypeId: ApiMethod<
  Server.CreateComponentByTypeIdInput,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_post_V1({
      ...data,
      languageId: 1,
    });

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const createComponentByTypeIdMultilingual: ApiMethod<
  Server.CreateComponentByTypeIdV2Input,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Components_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_Components_post_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const editComponent: ApiMethod<
  Server.EditComponentInput,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_put_V1({
      ...data,
      languageId: 1,
    });

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};

export const editComponentMultilingual: ApiMethod<
  Server.EditComponentV2Input,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  return thunkErrorHandler(
    async () => {
      const client = new Server.Server_Components_Client(
        undefined,
        contentClient
      );

      const response = await client.admin_Components_put_V2(data);

      if (response.success && response.data) {
        return response.data;
      }

      return rejectWithValue(response.errors);
    },
    dispatch,
    rejectWithValue
  );
};

export const cloneComponent: ApiMethod<
  Server.CloneComponentInput,
  string,
  void
> = async (data, { dispatch, rejectWithValue }) => {
  try {
    const client = new Server.Server_Components_Client(
      undefined,
      contentClient
    );

    const response = await client.components_clone_post_V1(data);

    if (response.success && response.data) {
      return response.data;
    }

    return rejectWithValue(response.errors);
  } catch (error: any) {
    const errors = JSON.parse(error.response).errors;

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.ERROR,
        messages: errors || [{ message: 'Something went wrong' }],
      })
    );

    return rejectWithValue(errors);
  }
};
